import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { TimeoutError, throwError } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

// Offline-first guardrail: without an explicit timeout, a request to a host that is
// completely unreachable (e.g. the VPS powered off, rather than actively refusing the
// connection) can hang on the underlying TCP connect for minutes with no error ever
// emitted — which is fatal for the one request that runs inside an APP_INITIALIZER
// (UserSettingsService.initialize()), since Angular does not render <app-root> until
// every initializer resolves. These timeouts guarantee every request eventually errors
// out so callers' existing catchError/offline-fallback logic can run.
const DEFAULT_TIMEOUT_MS = 20_000;
const LONG_TIMEOUT_MS = 60_000; // attachment upload/download — larger payloads
const AI_TIMEOUT_MS = 45_000; // LLM-backed endpoints — legitimately slower, still bounded

function resolveTimeoutMs(url: string): number {
  if (url.includes('/attachments')) {
    return LONG_TIMEOUT_MS;
  }
  if (url.includes('/ai/') || url.includes('/summary/') || url.includes('/similar')) {
    return AI_TIMEOUT_MS;
  }
  return DEFAULT_TIMEOUT_MS;
}

export const httpTimeoutInterceptor: HttpInterceptorFn = (req, next) => {
  const timeoutMs = resolveTimeoutMs(req.url);

  return next(req).pipe(
    timeout(timeoutMs),
    catchError((err: unknown) => {
      if (err instanceof TimeoutError) {
        return throwError(
          () =>
            new HttpErrorResponse({
              error: { message: `Request timed out after ${timeoutMs / 1000}s. The server may be unreachable.` },
              status: 0,
              statusText: 'Timeout',
              url: req.url,
            }),
        );
      }
      return throwError(() => err);
    }),
  );
};
